import { ImageWithCustomLoader } from '@components/image-with-custom-loader'
import { trackButtonClicked, TrackedButtonNames } from '@utils/analytics'
import { ValidStatusType } from '@utils/project'
import { BrandText, DetailText, HeadlineText } from 'design-configuration/components'
import React from 'react'

import { Button } from '@components/button'
import { PopUpCtaProjectFragment, ProjectCalendarFragment } from '@/gql/generated/graphql'
import { projectPurchaseRoute } from '@utils/routes'
import { useSoloViewer } from '@components/solo-viewer/provider'
import { ShopifyProduct } from '@utils/shopify'
import { ShopifyPurchaseButton } from './shopify-purchase'
import { AllPurposeLink } from '@components/all-purpose-link'
import { motion } from 'motion/react'
import { exhibitionTransition } from './header'

export const PopUpCTA = ({
  title,
  imgSrc,
  timer,
  footer,
  status
}: {
  title: string
  imgSrc: string
  timer?: React.ReactElement
  footer: React.ReactElement
  status?: ValidStatusType
}): React.ReactElement => {
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ ...exhibitionTransition, delay: 0 }}
      className="p-sm gap-y-xs bg-primary-20 relative flex h-fit w-full flex-col items-center rounded-[2px] backdrop-blur-[6px] sm:w-[298px]"
      style={{
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
      }}
    >
      <div className="text-center">
        {status === ValidStatusType.upcoming ? (
          <DetailText className="font-medium">NEXT RELEASE IN EXHIBITION</DetailText>
        ) : null}

        <HeadlineText className="font-light">{title}</HeadlineText>
      </div>
      <div className="relative aspect-square w-full">
        <ImageWithCustomLoader
          src={imgSrc}
          alt="Exhibition Pop Up CTA"
          style={{
            objectFit: 'cover'
          }}
          fill
          sizes="(max-width: 640px) 298px, 100vw"
        />
        {timer ? <div className="top-xs left-xs absolute">{timer}</div> : null}
      </div>
      {footer}
    </motion.div>
  )
}

export const ProjectPopUpCTAButtons = ({
  project,
  status,
  liveButtonText
}: {
  project?: PopUpCtaProjectFragment
  status: ValidStatusType
  liveButtonText?: string
}): React.ReactElement => {
  const { setSoloViewerData } = useSoloViewer()
  if (!project) return <></>
  return (
    <div className="gap-y-2xs flex w-full flex-col">
      {status === ValidStatusType.upcoming ? (
        <Button
          colorTheme="inverted-primary"
          className="px-xs w-full"
          branded
          onClick={() => {
            setSoloViewerData({
              token: project.featured_token?.[0],
              tokenList: [...project.tokens, ...project.oob_tokens]
            })
            trackButtonClicked(TrackedButtonNames.exhibitionPopUpCTA, {
              project: project,
              customProperties: {
                button: 'Explore algorithm',
                'Project id': project.id,
                'Project name': project.name,
                'Project status': status
              }
            })
          }}
        >
          <BrandText>Explore algorithm</BrandText>
        </Button>
      ) : status === ValidStatusType.live ? (
        <AllPurposeLink href={projectPurchaseRoute(project.slug)} className="w-full">
          <Button
            colorTheme="inverted-primary"
            className="px-xs w-full"
            branded
            onClick={() => {
              trackButtonClicked(TrackedButtonNames.exhibitionPopUpCTA, {
                project: project,
                customProperties: {
                  button: 'Proceed to purchase',
                  'Project id': project.id,
                  'Project name': project.name,
                  'Project status': status
                }
              })
            }}
          >
            <BrandText>{liveButtonText ?? 'Proceed to purchase'}</BrandText>
          </Button>
        </AllPurposeLink>
      ) : (
        <Button colorTheme="inverted-primary" className="px-xs w-full" branded disabled={true}>
          <BrandText>Sold Out</BrandText>
        </Button>
      )}
    </div>
  )
}

export const PhysicalPopUpCTAButtons = ({
  project,
  status,
  shopifyProduct,
  buttonTextOverride
}: {
  project: ProjectCalendarFragment
  status: ValidStatusType
  shopifyProduct: ShopifyProduct
  buttonTextOverride?: string
}): React.ReactElement => {
  return (
    <div className="gap-y-2xs flex w-full flex-col">
      {status === ValidStatusType.upcoming ? (
        <Button
          colorTheme="inverted-primary"
          className="px-xs w-full"
          branded
          disabled={true}
          onClick={() => {
            trackButtonClicked(TrackedButtonNames.exhibitionPopUpCTA, {
              project: project,
              customProperties: {
                button: 'Starting soon',
                'Project id': project.id,
                'Project name': project.name,
                'Project status': status
              }
            })
          }}
        >
          <BrandText>Starting soon</BrandText>
        </Button>
      ) : (
        <ShopifyPurchaseButton
          shopifyProduct={shopifyProduct}
          colorTheme="inverted-primary"
          branded
          buttonTextOverride={buttonTextOverride}
        />
      )}
    </div>
  )
}
